import { gql } from '@apollo/client'
import * as uuid from 'uuid'
import { useContext, useMemo } from 'react'

import AppContext from 'contexts/AppContext'
import Organization from 'models/Organization'
import useMutation from 'hooks/useMutation'
import useQuery from 'hooks/useQuery'
import useSearchInput from 'hooks/useSearchInput'
import { featureTypes, useAccountFeature } from 'hooks/useFeature'
import { License } from 'models/License'
import { permissions, useAccountPermissions } from 'hooks/usePermissions'

const accountCategory = {
  PROVIDER: 'doctor',
  ADMINISTRATOR: 'administrator'
}

const organizationAuthProviders = {
  OKTA: 'Okta'
}

const accountType = {
  PROVIDER: 'Provider',
  STAFF: 'Staff'
}

const DEFAULT_AI_DISCLAIMER_TEXT =
  'This note was AI-generated. Please review for accuracy.'

const patientReferenceValues = Object.freeze({
  THE_PATIENT: 'the_patient',
  PATIENT_NAME: 'patient_name'
})

const patientReferenceOptions = Object.freeze([
  { label: '“The patient”', value: patientReferenceValues.THE_PATIENT },
  { label: 'Patient name', value: patientReferenceValues.PATIENT_NAME }
])

const accountStatuses = Object.freeze({
  active: 'active',
  invited: 'invited',
  notInvited: 'not_invited',
  inactive: 'inactive'
})

const accountStatusColors = Object.freeze({
  active: 'accentGreen',
  invited: 'accentYellow',
  not_invited: 'accentGray',
  inactive: 'negative'
})

const accountLoginTypes = Object.freeze({
  DEFAULT: 'default',
  INTERNAL: 'internal'
})

const ACCOUNT_REGIONS = Object.freeze(['United States', 'Canada'])
const DB_USER_REGION_KEY = 'DB_USER_REGION'
const regionAcronyms = Object.freeze({
  'United States': 'us',
  Canada: 'ca'
})
const REGION_LANGUAGE = Object.freeze(['English'])
const DEFAULT_NOTE_LANGUAGES = Object.freeze([
  'English',
  'French',
  'Spanish'
])
const ADDITIONAL_NOTE_LANGUAGES = Object.freeze([
  'Italian',
  'Hindi',
  'Cantonese',
  'Mandarin',
  'Bengali',
  'Tamil',
  'Vietnamese',
  'Arabic',
  'Persian',
  'Hebrew'
])
const NOTE_LANGUAGES = Object.freeze([
  ...DEFAULT_NOTE_LANGUAGES,
  ...ADDITIONAL_NOTE_LANGUAGES
])

const languageAcronyms = Object.freeze({
  English: 'en',
  French: 'fr',
  Spanish: 'es',
  Italian: 'it',
  Hindi: 'hi',
  Cantonese: 'yue',
  Mandarin: 'cmn',
  Bengali: 'bn',
  Tamil: 'ta',
  Vietnamese: 'vi',
  Arabic: 'ar',
  Persian: 'fa',
  Hebrew: 'he'
})

const ACCOUNT_LANGUAGES = Object.freeze(['English', 'Français (French)'])
const DB_USER_LANGUAGE_KEY = 'DB_USER_LANGUAGE'
const switcherLanguageAcronyms = Object.freeze({
  English: 'en',
  'Français (French)': 'fr'
})

const isInternalLogin = account => {
  if (!account) return false

  return account.loginType === accountLoginTypes.INTERNAL
}

const isExternalLogin = account => {
  if (!account) return false

  return account.loginType === Organization.loginTypes.EXTERNAL
}

const isExternalRedirectLogin = account => {
  if (!account) return false

  return [Organization.loginTypes.SAML, Organization.loginTypes.OAUTH].includes(
    account.loginType
  )
}

const hasInternalPassword = account =>
  isInternalLogin(account) ||
  (!isExternalRedirectLogin(account) && account.type === accountType.STAFF)

const hasExternalLogin = account => {
  const { type } = account || {}

  return (
    isExternalRedirectLogin(account) ||
    (isExternalLogin(account) && type === accountType.PROVIDER)
  )
}

const ACCOUNT_SETTINGS_QUERY = gql`
  query AppQuery {
    currentAccount {
      id
      settings
    }
  }
`

const RESOLVED_FEATURES_QUERY = gql`
  query AppQuery {
    currentAccount {
      id
      resolvedFeatures
    }
  }
`

const RESOLVED_SETTINGS_QUERY = gql`
  query AppQuery {
    currentAccount {
      id
      resolvedSettings
    }
  }
`

const CURRENT_ACCOUNT_QUERY = gql`
  query AppQuery {
    currentAccount {
      id
      unreadNotificationsCount
      administrator
      alternateEmail
      alternatePhone
      category
      npi
      email
      eulaSignedAt
      externalLogin
      firstName
      fullName
      lastName
      loginType
      generatedReportUrl
      notificationDaysBeforeEvent
      phoneNumber
      practices {
        id
        name
      }
      resolvedFeatures
      resolvedSettings
      resolvedPermissions
      settings
      shareToken
      speciality
      status
      type
      externalLogin
      recordingFields
      resolvedNuanceUsername
      signatureLine
      signature

      licenses {
        ...License_details
      }

      organization {
        ...Organization_details
      }

      role {
        id
        name
      }
    }
  }

  ${License.fragments.details}
  ${Organization.fragments.details}
`

const ACCOUNTS_INFO_QUERY = gql`
  query AccountsInfoQuery($emails: [String!]!, $input: AccountsInfoInput) {
    accountsInfo(emails: $emails, input: $input) {
      id
      defaultTab
      email
      eulaSignedAt
      externalLogin
      externalLoginUrl
      loginType
      organizationAuthProvider
      organizationId
      organizationName
      organizationDisplayName
      status
      type
      maskedPhone
    }
  }
`

const SEND_VERIFICATION_CODE_MUTATION = gql`
  mutation SendVerificationCodeMutation($id: ID!) {
    sendVerificationCode(id: $id) {
      id
      email
      eulaSignedAt
      externalLogin
      loginType
      organizationId
      organizationName
      status
      type
    }
  }
`

const ACCOUNT_VERIFICATION_MUTATION = gql`
  mutation VerifyAccountMutation($id: ID!, $input: VerifyAccountInput!) {
    verifyAccount(id: $id, input: $input) {
      id
      email
      eulaSignedAt
      externalLogin
      loginType
      organizationId
      organizationName
      status
      type
    }
  }
`

const ACCOUNT_REGISTRATION_MUTATION = gql`
  mutation RegisterAccountMutation($id: ID!, $input: RegisterAccountInput!) {
    registerAccount(id: $id, input: $input) {
      id
      email
      eulaSignedAt
      externalLogin
      loginType
      organizationId
      organizationName
      status
      type
    }
  }
`

const UPDATE_ACCOUNT_SETTINGS_MUTATION = gql`
  mutation UpdateAccountSettingsMutation(
    $id: String!
    $input: UpdateAccountSettingsInput!
  ) {
    updateAccountSettings(accountId: $id, input: $input) {
      id
      settings
    }
  }
`

const LIST_CONNECTED_ACCOUNTS_QUERY = gql`
  query ConnectedAccounts($filter: ConnectedAccountFilterInput!) {
    connectedAccounts(filter: $filter) {
      id
      fullName
    }
  }
`

const quickFilterPageKeys = Object.freeze({
  ON_CALL: 'on-call',
  ON_CALL_SCHEDULE: 'on-call-schedule',
  SCHEDULE_CALENDAR: 'schedule-calendar',
  CLINIC_LIST: 'schedule-clinic-list',
  SURGERY_LIST: 'schedule-surgery-list',
  ROOMS: 'schedule-rooms',
  OP_NOTE_REPORTS: 'op-note-reports',
  ENTERPRISE_REPORTS: 'enterprise-reports'
})

const useQuickFilters = pageKey => {
  const {
    currentAccount: { id }
  } = useContext(AppContext)

  const {
    data: { currentAccount: { settings } = {} } = {},
    refetch: refetchSettings
  } = useQuery(ACCOUNT_SETTINGS_QUERY, {
    variables: { id },
    update: true
  })

  const [updateAccountSettings] = useMutation(
    UPDATE_ACCOUNT_SETTINGS_MUTATION,
    { onCompleted: () => refetchSettings() }
  )
  const filters =
    (settings && settings.quickFilters && settings.quickFilters[pageKey]) || []

  const methods = useMemo(
    () => ({
      createQuickFilter(name, filter) {
        let updatedSettings = {
          ...settings,
          quickFilters: settings.quickFilters || {}
        }

        updatedSettings = {
          ...updatedSettings,
          quickFilters: {
            ...updatedSettings.quickFilters,
            [pageKey]: updatedSettings.quickFilters[pageKey]
              ? [
                ...updatedSettings.quickFilters[pageKey],
                {
                  id: uuid.v4(),
                  name,
                  filter
                }
              ]
              : [
                {
                  id: uuid.v4(),
                  name,
                  filter
                }
              ]
          }
        }

        return updateAccountSettings({
          id,
          settings: updatedSettings
        })
      },

      destroyQuickFilter(settingId) {
        const updatedSettings = {
          ...settings,
          quickFilters: {
            ...settings.quickFilters,
            [pageKey]: settings.quickFilters[pageKey].filter(
              setting => setting.id !== settingId
            )
          }
        }

        return updateAccountSettings({
          id,
          settings: updatedSettings
        })
      }
    }),
    [id, pageKey, settings, updateAccountSettings]
  )

  return [filters, methods]
}

const useSelectCreator = () =>
  useSearchInput(
    LIST_CONNECTED_ACCOUNTS_QUERY,
    search => ({
      filter: {
        search
      }
    }),
    ({ connectedAccounts = [] }) =>
      connectedAccounts.map(user => ({ label: user.fullName, value: user.id }))
  )

const useDefaultTabOptions = account => {
  const useFeatureAndPermission = (feature, permission) => {
    const canUseAccountFeature = useAccountFeature(account, feature)
    const canUseAccountPermission = useAccountPermissions(account, permission)

    return canUseAccountFeature && canUseAccountPermission
  }

  const hasViewOnCallRecords = useFeatureAndPermission(
    featureTypes.VIEW_ON_CALL_RECORDS,
    permissions.VIEW_ON_CALL_RECORDS
  )

  const hasViewOnCallAvailabilities = useFeatureAndPermission(
    featureTypes.VIEW_ON_CALL_AVAILABILITIES,
    permissions.VIEW_ON_CALL_AVAILABILITIES
  )

  const hasViewVoiceCommands = useFeatureAndPermission(
    featureTypes.VIEW_VOICE_COMMANDS,
    permissions.VIEW_VOICE_COMMANDS
  )

  const canViewAdmin = account?.administrator

  const defaultTabOptions = [
    {
      label: 'Calendar',
      value: 'schedule',
      condition: useAccountFeature(account, featureTypes.VIEW_SCHEDULE)
    },
    {
      label: 'Surgery List',
      value: 'schedule_surgeries',
      condition: useAccountFeature(
        account,
        featureTypes.VIEW_SCHEDULE_SURGERIES
      )
    },
    {
      label: 'On Call Records',
      value: 'on_call_records',
      condition: hasViewOnCallRecords
    },
    {
      label: 'On Call Calendar',
      value: 'on_call_calendar',
      condition: hasViewOnCallAvailabilities
    },
    {
      label: 'Voice Commands',
      value: 'voice_commands',
      condition: hasViewVoiceCommands
    },
    { label: 'Admin', value: 'admin', condition: canViewAdmin }
  ].filter(option => option.condition)

  return defaultTabOptions
}

const automaticAiNoteDeletionOptions = [
  { label: 'Disabled', value: 'disabled' },
  { label: 'Upon marking note Complete', value: 'upon_complete' },
  { label: 'After 1 day', value: 'after_1_day' },
  { label: 'After 7 days', value: 'after_7_days' },
  { label: 'After 14 days', value: 'after_14_days' },
  { label: 'After 30 days', value: 'after_30_days' },
  { label: 'After 60 days', value: 'after_60_days' }
]

export {
  ACCOUNT_LANGUAGES,
  ACCOUNT_REGIONS,
  ACCOUNT_REGISTRATION_MUTATION,
  ACCOUNT_SETTINGS_QUERY,
  ACCOUNT_VERIFICATION_MUTATION,
  accountCategory,
  ACCOUNTS_INFO_QUERY,
  accountStatusColors,
  accountStatuses,
  accountType,
  ADDITIONAL_NOTE_LANGUAGES,
  automaticAiNoteDeletionOptions,
  CURRENT_ACCOUNT_QUERY,
  DEFAULT_AI_DISCLAIMER_TEXT,
  DEFAULT_NOTE_LANGUAGES,
  DB_USER_LANGUAGE_KEY,
  DB_USER_REGION_KEY,
  hasExternalLogin,
  hasInternalPassword,
  isExternalLogin,
  isExternalRedirectLogin,
  isInternalLogin,
  languageAcronyms,
  LIST_CONNECTED_ACCOUNTS_QUERY,
  NOTE_LANGUAGES,
  organizationAuthProviders,
  patientReferenceOptions,
  patientReferenceValues,
  quickFilterPageKeys,
  REGION_LANGUAGE,
  regionAcronyms,
  RESOLVED_FEATURES_QUERY,
  RESOLVED_SETTINGS_QUERY,
  SEND_VERIFICATION_CODE_MUTATION,
  switcherLanguageAcronyms,
  UPDATE_ACCOUNT_SETTINGS_MUTATION,
  useDefaultTabOptions,
  useQuickFilters,
  useSelectCreator
}
